















































import Vue from 'vue';

import { OSNotification } from 'onesignal-cordova-plugin';
import { IUser } from '@/types';
import { BonusMessage } from '../types';

import { appStoreMapper } from '@/store/appstore';
import { formatNumber } from '@/utils/formatNumber';
import bus from '@/bus';

import BonusIcon from '@/components/bonus-icon.vue';

export default Vue.extend({
  name: 'BonusMessage',
  components: { BonusIcon },
  data: () => ({
    messages: [] as BonusMessage[],
    message: null as Nullable<BonusMessage>,
    visible: false,
    touchStart: null as Nullable<number>,
    touchDiffX: 0,
  }),
  mounted() {
    bus.$on('bonus-push', this.onNewMessage);

    bus.$on('hideMessage', () => {
      this.nextMessage();
    });
  },
  computed: {
    bonusText(): string {
      if (!this.message) {
        return '';
      }

      switch (this.message.messageCode) {
        case 'bonus-in':
          return this.$t('modules.bonus.messageCode.bonusIn').toString();
        case 'bonus-out':
          return this.$t('modules.bonus.messageCode.bonusOut').toString();
        case 'bonus-in-delay': {
          const days =
            this.message.bonusDelay +
            ' ' +
            this.$tc(
              'global.days.plural',
              this.string2Number(this.message.bonusDelay),
            ).toString();
          return this.$t('modules.bonus.messageCode.bonusInDelay', [days]).toString();
        }
        case 'bonus-out-delay': {
          const days =
            this.message.bonusDelay +
            ' ' +
            this.$tc(
              'global.days.plural',
              this.string2Number(this.message.bonusDelay),
            ).toString();
          return this.$t('modules.bonus.messageCode.bonusOutDelay', [days]).toString();
        }
        case 'bonus-out-in-delay': {
          const bonus =
            Math.abs(this.string2Number(this.message.bonusOut)) +
            ' ' +
            this.$tc(
              'global.bonus.plural',
              this.string2Number(this.message.bonusOut),
            ).toString();
          const days =
            this.message.bonusDelay +
            ' ' +
            this.$tc(
              'global.days.plural',
              this.string2Number(this.message.bonusDelay),
            ).toString();
          return this.$t('modules.bonus.messageCode.bonusOutInDelay', [
            bonus,
            days,
          ]).toString();
        }
        case 'bonus-register':
          return this.$t('modules.bonus.messageCode.bonusRegister').toString();
        case 'bonus-gift':
          return this.$t('modules.bonus.messageCode.bonusGift').toString();
        case 'bonus-balance':
          return this.$t('modules.bonus.messageCode.bonusBalance').toString();
        default:
          const exhaustiveCheck: never = this.message.messageCode;
          return exhaustiveCheck;
      }
    },
    bonusAmount(): number {
      if (!this.message) {
        return 0;
      }

      switch (this.message.messageCode) {
        case 'bonus-in':
          return this.string2Number(this.message.bonusIn);
        case 'bonus-out':
          return this.string2Number(this.message.bonusOut);
        case 'bonus-in-delay':
          return this.string2Number(this.message.bonusIn);
        case 'bonus-out-delay':
          return this.string2Number(this.message.bonusOut);
        case 'bonus-out-in-delay':
          return this.string2Number(this.message.bonusIn);
        case 'bonus-balance':
          return this.string2Number(this.message.bonusAmount);
        case 'bonus-gift':
          return this.string2Number(this.message.bonusAmount);
        case 'bonus-register':
          return this.string2Number(this.message.bonusAmount);
        default:
          const exhaustiveCheck: never = this.message.messageCode;
          return exhaustiveCheck;
      }
    },
    amountFormatted(): string {
      return formatNumber(
        Math.abs(this.bonusAmount),
        this.decimalOptions.decimalRoundingRule,
        this.decimalOptions.decimal,
      );
    },
    ...appStoreMapper.mapGetters(['decimalOptions', 'user']),
  },
  methods: {
    string2Number(num: Nullable<string | number>): number {
      return typeof num === 'string' ? parseFloat(num) : num ?? 0;
    },
    onNewMessage(notification: OSNotification) {
      const message = notification.additionalData as Nullable<BonusMessage>;

      if (message) {
        this.messages.push(message);
      }
    },
    nextMessage() {
      const container = this.$refs.container as HTMLElement;

      if (container && this.messages.length > 1) {
        (this.$refs.container as HTMLElement).classList.add('quick');
      }

      this.visible = false;

      bus.$emit('messageClosed');
    },
    skipAllMessages() {
      this.visible = false;

      this.message = null;
      this.messages = [];

      bus.$emit('messageClosed');
    },
    afterLeave() {
      this.message = null;
      this.messages.shift();
    },
    startHandler(e: TouchEvent) {
      const startX = e.touches?.[0]?.clientX;

      if (startX != null) {
        this.touchStart = e.touches[0].clientX;
      }
    },
    endHandler() {
      const container = this.$refs.container as HTMLElement;

      if (this.touchStart && this.touchDiffX) {
        this.touchStart = null;

        if (Math.abs(this.touchDiffX) > 75) {
          container.classList.add('quick');

          this.$nextTick(() => {
            this.nextMessage();
          });
        } else {
          container.classList.add('animated');

          this.$nextTick(() => {
            container.style.transform = `translateX(0)`;
            container.style.opacity = `1`;

            setTimeout(() => {
              container.classList.remove('animated');
            }, 500);
          });
        }

        this.touchStart = null;
        this.touchDiffX = 0;
      }
    },
    movingHandler(e: TouchEvent) {
      if (this.touchStart != null) {
        this.touchDiffX = e.touches[0].clientX - this.touchStart;

        const opacity = (75 - Math.abs(this.touchDiffX)) / 75;

        (
          this.$refs.container as HTMLElement
        ).style.transform = `translateX(${this.touchDiffX}px)`;
        (this.$refs.container as HTMLElement).style.opacity = opacity.toFixed(2);
      }
    },
    ...appStoreMapper.mapMutations(['setUser']),
  },
  watch: {
    messages() {
      const messageToShow = this.messages[0];

      if (messageToShow) {
        this.visible = true;
        this.message = messageToShow;

        if (messageToShow.bonusNeedFeedback) {
          if (messageToShow.feedbackBranchId) {
            const user = JSON.parse(JSON.stringify(this.user)) as IUser;
            user.lastVisitedBranchId =
              typeof messageToShow.feedbackBranchId === 'string'
                ? parseInt(messageToShow.feedbackBranchId, 10)
                : messageToShow.feedbackBranchId;
            this.setUser(user);
          }

          bus.$emit('feedback:sheet:show', {
            bonusId: messageToShow.idConnectedEntity,
            branchId: messageToShow.feedbackBranchId,
          });
        }
      }
    },
  },
});
